import React, { useEffect, useMemo, useState } from 'react'
import { getApiUrl, apiRequest } from '../utils/api'

const INITIAL_VISIBLE = 25

const formatNumber = (n) => {
  if (n === null || n === undefined) return 'N/A'
  const cleaned = typeof n === 'string' ? n.replace(/,/g, '').trim() : n
  const num = Number(cleaned)
  if (Number.isNaN(num)) return n
  return num.toLocaleString()
}

export default function ProfileInfoData({ campaigns, profileCache }) {
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE)

  // Helpers (mirror parsing used in CampaignCompletionForm)
  const getNested = (obj, pathArr) => {
    try {
      return pathArr.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj)
    } catch { return undefined }
  }
  const getUsername = (info) => (
    info?.username || info?.userName || info?.name ||
    getNested(info, ['user','username']) ||
    getNested(info, ['owner','username']) ||
    getNested(info, ['author','username']) ||
    getNested(info, ['graphql','user','username']) ||
    getNested(info, ['profile','username']) || ''
  )
  const getFollowers = (info) => (
    info?.followersCount || info?.followers || info?.followerCount ||
    getNested(info, ['graphql','user','edge_followed_by','count']) ||
    getNested(info, ['user','edge_followed_by','count']) ||
    getNested(info, ['statistics','followers']) ||
    getNested(info, ['counts','followers']) ||
    getNested(info, ['edge_followed_by','count']) || undefined
  )
  const getFollowing = (info) => (
    info?.followingCount || info?.following || info?.followsCount ||
    getNested(info, ['graphql','user','edge_follow','count']) ||
    getNested(info, ['user','edge_follow','count']) ||
    getNested(info, ['statistics','following']) ||
    getNested(info, ['counts','following']) ||
    getNested(info, ['edge_follow','count']) || undefined
  )
  const getPosts = (info) => (
    info?.postsCount || info?.mediaCount || info?.media_count ||
    getNested(info, ['graphql','user','edge_owner_to_timeline_media','count']) ||
    getNested(info, ['user','media','count']) || undefined
  )
  const getFullName = (info) => (
    info?.fullName || info?.full_name || info?.name ||
    getNested(info, ['graphql','user','full_name']) ||
    getNested(info, ['user','full_name']) || undefined
  )
  const getBio = (info) => (
    info?.bio || info?.biography || info?.description ||
    getNested(info, ['graphql','user','biography']) || ''
  )
  const getProfileUrl = (info, fallbackUsername) => (
    info?.url || info?.profile_url || (fallbackUsername ? `https://www.instagram.com/${fallbackUsername}/` : null)
  )

  const buildProfilesFromRows = (rows) => {
    const map = new Map()
    for (const row of rows) {
      let info = row?.influencer_info
      if (typeof info === 'string') {
        try { info = JSON.parse(info) } catch { continue }
      }
      const username = getUsername(info) || row?.influencer_username || row?.influencer_name
      if (!username) continue
      if (!map.has(username)) {
        map.set(username, {
          username,
          fullName: (info && getFullName(info)) || row?.influencer_name || 'N/A',
          followersCount: info ? getFollowers(info) ?? null : null,
          followingCount: info ? getFollowing(info) ?? null : null,
          postsCount: info ? getPosts(info) ?? null : null,
          private: info ? (info.private ?? null) : null,
          verified: info ? (info.verified ?? null) : null,
          isBusinessAccount: info ? (info.isBusinessAccount ?? null) : null,
          url: getProfileUrl(info, username),
          externalUrl: info ? (info.externalUrl || null) : null,
          bio: info ? getBio(info) : ''
        })
      }
    }
    return Array.from(map.values())
  }

  // Seed profiles immediately from profileCache (latest extraction)
  useEffect(() => {
    if (!profileCache) return
    const entries = Object.entries(profileCache)
    if (entries.length === 0) return
    const rows = entries.map(([username, info]) => ({ influencer_username: username, influencer_info: info }))
    const seeded = buildProfilesFromRows(rows)
    if (seeded.length > 0) {
      setProfiles(prev => {
        const existing = new Map(prev.map(p => [p.username, p]))
        for (const s of seeded) existing.set(s.username, { ...existing.get(s.username), ...s })
        return Array.from(existing.values())
      })
    }
  }, [profileCache])

  // Always reconcile with latest profileCache (overwrite N/A values)
  useEffect(() => {
    if (!profileCache) return
    const entries = Object.entries(profileCache)
    if (entries.length === 0) return
    setProfiles(prev => {
      const byUser = new Map(prev.map(p => [p.username, p]))
      for (const [uname, info] of entries) {
        const u = uname
        const existing = byUser.get(u) || { username: u }
        const fullName = getFullName(info) || existing.fullName
        const followersCount = getFollowers(info) ?? existing.followersCount
        const followingCount = getFollowing(info) ?? existing.followingCount
        const postsCount = getPosts(info) ?? existing.postsCount
        const url = getProfileUrl(info, u) || existing.url
        const externalUrl = info?.externalUrl || existing.externalUrl
        const bio = getBio(info) || existing.bio
        byUser.set(u, {
          ...existing,
          username: u,
          fullName: fullName || 'N/A',
          followersCount,
          followingCount,
          postsCount,
          private: info?.private ?? existing.private ?? null,
          verified: info?.verified ?? existing.verified ?? null,
          isBusinessAccount: info?.isBusinessAccount ?? existing.isBusinessAccount ?? null,
          url,
          externalUrl,
          bio
        })
      }
      return Array.from(byUser.values())
    })
  }, [profileCache])

  // If campaigns are passed from parent, derive immediately and on changes
  useEffect(() => {
    if (Array.isArray(campaigns) && campaigns.length > 0) {
      const built = buildProfilesFromRows(campaigns)
      setProfiles(prev => {
        const byUser = new Map(prev.map(p => [p.username, p]))
        for (const p of built) {
          const existing = byUser.get(p.username)
          if (!existing) {
            byUser.set(p.username, p)
            continue
          }
          byUser.set(p.username, {
            ...existing,
            // prefer non-null values; fill from campaigns if missing
            fullName: existing.fullName || p.fullName,
            followersCount: existing.followersCount ?? p.followersCount,
            followingCount: existing.followingCount ?? p.followingCount,
            postsCount: existing.postsCount ?? p.postsCount,
            private: existing.private ?? p.private,
            verified: existing.verified ?? p.verified,
            isBusinessAccount: existing.isBusinessAccount ?? p.isBusinessAccount,
            url: existing.url || p.url,
            externalUrl: existing.externalUrl || p.externalUrl,
            bio: existing.bio || p.bio,
          })
        }
        return Array.from(byUser.values())
      })
    }
  }, [campaigns])

  // Fallback: fetch from API when no campaigns prop or empty
  useEffect(() => {
    if (Array.isArray(campaigns) && campaigns.length > 0) return
    const run = async () => {
      setLoading(true)
      setError('')
      try {
        const resp = await apiRequest(`/campaigns`)
        const data = await resp.json()
        if (!data.success) throw new Error(data.error || 'Failed to fetch')
        const built = buildProfilesFromRows(data.campaigns || [])
        setProfiles(prev => {
          const byUser = new Map(prev.map(p => [p.username, p]))
          for (const p of built) {
            const existing = byUser.get(p.username)
            if (!existing) { byUser.set(p.username, p); continue }
            byUser.set(p.username, {
              ...existing,
              fullName: existing.fullName || p.fullName,
              followersCount: existing.followersCount ?? p.followersCount,
              followingCount: existing.followingCount ?? p.followingCount,
              postsCount: existing.postsCount ?? p.postsCount,
              private: existing.private ?? p.private,
              verified: existing.verified ?? p.verified,
              isBusinessAccount: existing.isBusinessAccount ?? p.isBusinessAccount,
              url: existing.url || p.url,
              externalUrl: existing.externalUrl || p.externalUrl,
              bio: existing.bio || p.bio,
            })
          }
          return Array.from(byUser.values())
        })
      } catch (e) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [campaigns])

  // Enrich missing profiles by calling backend /profile-info for usernames
  useEffect(() => {
    const run = async () => {
      // Find usernames that lack followersCount
      const toFetch = profiles.filter(p => p.username && (p.followersCount === null || p.followersCount === undefined)).map(p => p.username)
      if (toFetch.length === 0) return
      try {
        // Fetch sequentially to avoid rate limits
        const updates = new Map()
        for (const uname of toFetch) {
          try {
            const resp = await apiRequest(`/profile-info2`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: uname })
            })
            const data = await resp.json()
            const info = data.profile_info || data.data
            if (data.success && info) {
              updates.set(uname, info)
            }
          } catch (_) { /* ignore single failures */ }
        }
        if (updates.size > 0) {
          setProfiles(prev => prev.map(p => {
            const up = updates.get(p.username)
            if (!up) return p
            return {
              ...p,
              fullName: up.fullName || up.name || p.fullName,
              followersCount: up.followersCount ?? p.followersCount,
              followingCount: up.followingCount ?? p.followingCount,
              postsCount: up.postsCount ?? p.postsCount,
              private: up.private ?? p.private,
              verified: up.verified ?? p.verified,
              isBusinessAccount: up.isBusinessAccount ?? p.isBusinessAccount,
              url: up.url || p.url,
              externalUrl: up.externalUrl || p.externalUrl,
              bio: up.bio || up.biography || p.bio
            }
          }))
        }
      } catch (_) { /* noop */ }
    }
    if (profiles && profiles.length > 0) run()
  }, [profiles])

  const filtered = useMemo(() => {
    if (!query) return profiles
    const q = query.toLowerCase()
    return profiles.filter(p => (
      (p.username || '').toLowerCase().includes(q) ||
      (p.fullName || '').toLowerCase().includes(q) ||
      (p.bio || '').toLowerCase().includes(q)
    ))
  }, [profiles, query])

  const visibleProfiles = filtered.slice(0, visibleCount)

  const handleShowMore = () => {
    setVisibleCount(prev => Math.min(prev + INITIAL_VISIBLE, filtered.length))
  }

  const handleShowLess = () => {
    setVisibleCount(INITIAL_VISIBLE)
  }

  const handleDeleteProfile = (username) => {
    if (!window.confirm(`Remove profile "${username}" from the list?`)) return
    setProfiles(prev => prev.filter(p => p.username !== username))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Profile Information Data</h2>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search username, name or bio..."
          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-72"
        />
      </div>

      {loading && (
        <div className="text-gray-600">Loading profiles...</div>
      )}
      {error && (
        <div className="text-red-600">{error}</div>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">Full Name</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Followers</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Following</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Posts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Verified</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Business</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Links</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visibleProfiles.map((p) => (
                <tr key={p.username} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 break-all">{p.username}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 break-all">{p.fullName || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-right tabular-nums">{formatNumber(p.followersCount)}</td>
                  <td className="px-4 py-3 text-sm text-right tabular-nums">{formatNumber(p.followingCount)}</td>
                  <td className="px-4 py-3 text-sm text-right tabular-nums">{formatNumber(p.postsCount)}</td>
                  <td className="px-4 py-3 text-sm">{p.verified ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3 text-sm">{p.isBusinessAccount ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100">Profile</a>
                    )}
                    {p.externalUrl && (
                      <a href={p.externalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 py-1 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100">External</a>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    <button
                      onClick={() => handleDeleteProfile(p.username)}
                      className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 text-xs font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {visibleProfiles.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-sm text-gray-500">No profiles found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Show more/less controls */}
      {!loading && !error && filtered.length > 0 && (
        <div className="flex flex-col items-center justify-center gap-2 px-2 py-4 text-sm text-gray-600">
          <span>
            Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} profiles
          </span>
          <div className="flex items-center gap-2">
            {visibleCount < filtered.length && (
              <button
                onClick={handleShowMore}
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Show more
              </button>
            )}
            {visibleCount > INITIAL_VISIBLE && (
              <button
                onClick={handleShowLess}
                className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-800 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
              >
                Show less
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


